/**
 * HTTP module for the intercorrent peticionamento wizard.
 *
 * Contract aligned with the backend (Phase 3 of the plan). Kept separate from
 * `client.ts` per the modularization guideline (one module per domain).
 */
import { api, type PaginatedResponse, type PaginationParams } from './client';
import type {
  ConfirmAnexoBody,
  ConfirmPrincipalBody,
  PatchAnexoBody,
  PatchRascunhoBody,
  Peticao,
  PeticaoAnexo,
  PeticaoElegibilidade,
  PeticaoStatus,
  PeticaoUploadIntent,
  PresignResponse,
  ReciboPeticao,
  ReordenarAnexoItem,
} from '@/components/peticionamento/types';

const BASE = '/peticoes';

export async function fetchElegibilidade(processoId: string): Promise<PeticaoElegibilidade> {
  const { data } = await api.get<PeticaoElegibilidade>(`${BASE}/elegibilidade`, {
    params: { processo_id: processoId },
  });
  return data;
}

/**
 * Tramitação de um processo, exposta pelo backend para o seletor do wizard.
 * Espelha ``TramitacaoDisponivelOut`` do Pydantic.
 */
export interface TramitacaoDisponivel {
  id_fonte_dados_codex: string;
  id_origem: string | null;
  /** Data de ajuizamento — alimenta a mesma desambiguação das abas dos autos. */
  data_hora_ajuizamento: string | null;
  /** Código TPU da classe — resolve a sigla oficial via classesTpuSiglas. */
  classe_codigo?: number | null;
  instancia: string | null;
  grau: string | null;
  tribunal_sigla: string | null;
  sistema_processual: string | null;
  /** Descrição da classe processual — usada como rótulo principal no seletor. */
  classe_descricao: string | null;
  ativo: boolean;
  permite_peticionar: boolean;
  eh_tramitacao_atual: boolean;
}

/**
 * Lista as tramitações disponíveis no processo para o seletor de
 * peticionamento. Usado em processos multi-grau (ex.: G1 + G2).
 */
export async function listTramitacoesDisponiveis(
  processoId: string,
): Promise<TramitacaoDisponivel[]> {
  const { data } = await api.get<TramitacaoDisponivel[]>(
    `${BASE}/tramitacoes-disponiveis`,
    { params: { processo_id: processoId } },
  );
  return data;
}

/**
 * Body do ``POST /peticoes/rascunho``.
 *
 * ``id_fonte_dados_codex`` é opcional: quando informado, força o backend a
 * criar rascunho na tramitação indicada (processos multi-grau). Quando
 * omitido, o backend usa a ``tramitacao_atual``.
 */
export interface RascunhoCreateBody {
  draft_key: string;
  processo_id: string;
  id_fonte_dados_codex?: string;
}

export async function createRascunho(body: RascunhoCreateBody): Promise<Peticao> {
  const { data } = await api.post<Peticao>(`${BASE}/rascunho`, body);
  return data;
}

export async function patchRascunho(
  peticaoId: string,
  body: PatchRascunhoBody,
): Promise<Peticao> {
  const { data } = await api.patch<Peticao>(`${BASE}/${peticaoId}/rascunho`, body);
  return data;
}

export async function presignPrincipal(
  peticaoId: string,
  body: PeticaoUploadIntent,
): Promise<PresignResponse> {
  const { data } = await api.post<PresignResponse>(
    `${BASE}/${peticaoId}/principal/presign`,
    body,
  );
  return data;
}

export async function confirmPrincipal(
  peticaoId: string,
  body: ConfirmPrincipalBody,
): Promise<Peticao> {
  const { data } = await api.post<Peticao>(`${BASE}/${peticaoId}/principal/confirm`, body);
  return data;
}

export async function presignAnexo(
  peticaoId: string,
  body: PeticaoUploadIntent,
): Promise<PresignResponse> {
  const { data } = await api.post<PresignResponse>(
    `${BASE}/${peticaoId}/anexos/presign`,
    body,
  );
  return data;
}

export async function confirmAnexo(
  peticaoId: string,
  body: ConfirmAnexoBody,
): Promise<PeticaoAnexo> {
  const { data } = await api.post<PeticaoAnexo>(`${BASE}/${peticaoId}/anexos/confirm`, body);
  return data;
}

export async function deleteAnexo(peticaoId: string, anexoId: string): Promise<void> {
  await api.delete(`${BASE}/${peticaoId}/anexos/${anexoId}`);
}

/**
 * Atualiza metadata de um anexo já confirmado. Usado pelo upload eager
 * quando o usuário muda tipo/descrição/sigiloso após o blob ter sido
 * enviado. Reordenação fica fora deste endpoint.
 */
export async function patchAnexo(
  peticaoId: string,
  anexoId: string,
  body: PatchAnexoBody,
): Promise<PeticaoAnexo> {
  const { data } = await api.patch<PeticaoAnexo>(
    `${BASE}/${peticaoId}/anexos/${anexoId}`,
    body,
  );
  return data;
}

/**
 * Reordena os anexos da petição em uma única transação.
 *
 * Frontend envia a lista completa de todos os anexos com seus novos valores
 * de ``ordem``. Cada item identifica um anexo por ``anexo_id`` ou
 * ``client_upload_id`` (este último permite reordenar mesmo enquanto a
 * resposta do confirm ainda não propagou pra UI).
 */
export async function reordenarAnexos(
  peticaoId: string,
  ordens: ReordenarAnexoItem[],
): Promise<PeticaoAnexo[]> {
  const { data } = await api.post<PeticaoAnexo[]>(
    `${BASE}/${peticaoId}/anexos/reordenar`,
    { ordens },
  );
  return data;
}

/**
 * Returns the preview PDF as a Blob (same weasyprint pipeline as the final).
 * Used in the Revisao step of the wizard.
 */
export async function fetchPreview(peticaoId: string): Promise<Blob> {
  const response = await api.get(`${BASE}/${peticaoId}/preview`, {
    responseType: 'blob',
  });
  return response.data as Blob;
}

/**
 * Alias semantico para a pagina de detalhe, que abre o PDF como objectURL.
 */
export async function fetchPreviewPdf(peticaoId: string): Promise<Blob> {
  return fetchPreview(peticaoId);
}

/**
 * Minuta do editor em DOCX — mesmo corpo e mesmo timbrado do preview em PDF,
 * em formato editável. Só existe no modo EDITOR (no modo PDF o documento é o
 * arquivo enviado pelo usuário, e o backend responde 400).
 */
export async function fetchMinutaDocx(peticaoId: string): Promise<Blob> {
  const response = await api.get(`${BASE}/${peticaoId}/minuta.docx`, {
    responseType: 'blob',
  });
  return response.data as Blob;
}

export async function submitPeticao(
  peticaoId: string,
  idempotencyKey: string,
): Promise<Peticao> {
  const { data } = await api.post<Peticao>(`${BASE}/${peticaoId}/submit`, {
    idempotency_key: idempotencyKey,
  });
  return data;
}

export async function fetchPeticao(peticaoId: string): Promise<Peticao> {
  const { data } = await api.get<Peticao>(`${BASE}/${encodeURIComponent(peticaoId)}`);
  return data;
}

/**
 * Filtros aceitos por `listPeticoesGlobal`. Todos mapeiam para a querystring
 * do backend (repeat para arrays, ver `paramsSerializer` abaixo).
 */
export interface PeticaoListFilters {
  status?: PeticaoStatus[];
  processoId?: string;
  numeroProcesso?: string;
}

/**
 * Lista paginada de petições do usuário autenticado.
 *
 * Usa `paramsSerializer: { indexes: null }` para serializar arrays em formato
 * repeat (`status=A&status=B`), que é o formato que FastAPI espera para
 * query params múltiplos. Sem isso, axios 1.x serializa como
 * `status[]=A&status[]=B` (brackets), que o FastAPI não reconhece.
 */
export async function listPeticoesGlobal(
  params?: PeticaoListFilters & PaginationParams,
): Promise<PaginatedResponse<Peticao>> {
  const { data } = await api.get<PaginatedResponse<Peticao>>(BASE, {
    params: {
      status: params?.status,
      processo_id: params?.processoId,
      numero_processo: params?.numeroProcesso,
      page: params?.page,
      page_size: params?.page_size,
      order_by: params?.order_by,
      order_dir: params?.order_dir,
    },
    paramsSerializer: { indexes: null },
  });
  return data;
}

/**
 * Lista petições de um processo específico. Retrocompat com o wizard —
 * para a tela global, prefira `listPeticoesGlobal`.
 */
export async function listPeticoes(processoId: string): Promise<Peticao[]> {
  const response = await listPeticoesGlobal({ processoId, page_size: 100 });
  return response.items;
}

export interface PeticaoCountsResp {
  total: number;
  andamento: number;
  protocoladas: number;
  falhas: number;
  rascunhos: number;
}

/**
 * Contadores agregados das petições do usuário, usados nos StatCards da
 * página de listagem global.
 */
export async function fetchPeticaoCounts(): Promise<PeticaoCountsResp> {
  const { data } = await api.get<PeticaoCountsResp>(`${BASE}/counts`);
  return data;
}

/**
 * Presigned URL (GET) para baixar um anexo específico de uma petição.
 * O backend confere a propriedade do recurso e emite URL curta (tipicamente 15 min).
 */
export async function getAnexoDownloadUrl(
  peticaoId: string,
  anexoId: string,
): Promise<{ url: string; expires_at: string }> {
  const { data } = await api.get<{ url: string; expires_at: string }>(
    `${BASE}/${peticaoId}/anexos/${anexoId}/download`,
  );
  return data;
}

/**
 * Presigned URL (GET) para baixar o documento principal (PDF consolidado
 * ou upload PDF bruto) de uma petição.
 */
export async function getPrincipalDownloadUrl(
  peticaoId: string,
): Promise<{ url: string; expires_at: string }> {
  const { data } = await api.get<{ url: string; expires_at: string }>(
    `${BASE}/${peticaoId}/principal/download`,
  );
  return data;
}

/**
 * Reenfileira uma peticao em estado FALHA_FINAL, FALHA_ELEGIBILIDADE ou
 * AGUARDANDO_CONFERENCIA_MANUAL.
 */
export async function retryPeticao(peticaoId: string): Promise<Peticao> {
  const { data } = await api.post<Peticao>(`${BASE}/${peticaoId}/retry`);
  return data;
}

/**
 * Reabre uma petição em FALHA_FINAL/FALHA_ELEGIBILIDADE/AGUARDANDO_CONFERENCIA_MANUAL
 * para edição: limpa anexos rejeitados pelo tribunal (no jurisapp e no
 * autenticador) e devolve o status para RASCUNHO. CTA típico em
 * ``tipo_falha === 'DOCUMENTO_RECUSADO'``.
 */
export async function voltarPeticaoParaRascunho(peticaoId: string): Promise<Peticao> {
  const { data } = await api.post<Peticao>(`${BASE}/${peticaoId}/voltar-para-rascunho`);
  return data;
}

/**
 * Força sincronização imediata do status com o autenticador (bypass do
 * backoff progressivo do worker de polling). Em estado terminal devolve o
 * snapshot atual sem round-trip externo. Use no botão "Atualizar agora"
 * da tela de detalhe — o polling automático pode estar em janela de até 5min.
 */
export async function refreshPeticaoStatus(peticaoId: string): Promise<Peticao> {
  const { data } = await api.post<Peticao>(`${BASE}/${peticaoId}/refresh-status`);
  return data;
}

/**
 * Exclui um rascunho do usuário. O backend confere ownership e exige status
 * RASCUNHO — qualquer outro estado retorna 409.
 */
export async function deletePeticaoRascunho(peticaoId: string): Promise<void> {
  await api.delete(`${BASE}/${peticaoId}`);
}

/**
 * Quem preparou a petição na conta do dono (processo de colega ou delegação) avisa, por
 * e-mail, quem protocola: os responsáveis que podem protocolar ou, sem nenhum, o dono.
 * Devolve os nomes de quem foi avisado.
 */
export async function avisarDono(peticaoId: string): Promise<string[]> {
  const { data } = await api.post<{ avisados: string[] }>(`${BASE}/${peticaoId}/avisar-dono`);
  return data.avisados;
}

/**
 * Busca o recibo CNJ de uma petição protocolada. Retorna uma presigned URL
 * (válida por ~15 min) para download do PDF, além de metadados (número CNJ,
 * hash, timestamp de atualização). Falha com 409 se status != PROTOCOLADA e
 * com 404 (detail contendo RECIBO_NAO_CAPTURADO) quando o recibo ainda não
 * foi baixado do portal — neste caso o caller deve disparar refreshRecibo.
 */
export async function fetchRecibo(peticaoId: string): Promise<ReciboPeticao> {
  const { data } = await api.get<ReciboPeticao>(`${BASE}/${peticaoId}/recibo`);
  return data;
}

/**
 * Re-captura o recibo CNJ no portal do tribunal e persiste em S3. Usado
 * tanto por atualização manual (usuário quer recibo mais recente) quanto
 * como fallback automático quando fetchRecibo retorna RECIBO_NAO_CAPTURADO.
 * Pode levar alguns segundos (autentica PJe, baixa, faz upload).
 */
export async function refreshRecibo(peticaoId: string): Promise<ReciboPeticao> {
  const { data } = await api.post<ReciboPeticao>(`${BASE}/${peticaoId}/recibo/refresh`);
  return data;
}

/**
 * Re-exporta os helpers neutros de upload (`sha256OfBlob`, `uploadBlobToS3`)
 * para preservar os call sites existentes (`syncManager.ts` e demais
 * consumidores que importam de `@/api/peticoes`). A implementação vive em
 * `@/lib/upload` e é compartilhada com o upload genérico de artefatos.
 */
export { sha256OfBlob, uploadBlobToS3 } from '@/lib/upload';
